// preact
import { h, Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';

// utils
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { edgesToNodes } from 'utils/storefront/format-resource';
import ProductCard from 'utils/product-card';
import Panel from 'utils/panel';
import FiltersIcon from 'utils/icon/icon-filters.svg';
import SortingIcon from 'utils/icon/icon-sorting.svg';
import CloseIcon from 'utils/icon/icon-close.svg';

// components
import Accordions from '../product/product-accordions';
import filtersArray from './filters-data';
import SelectedFilters from './selected-filters';

// hooks
import useCustomizerStyleModal from '../../hooks/useCustomizerStyleModal';

// ProductGrid component
const ProductGrid = (props) => {
  const storefront = Storefront.getInstance();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState(props.collection.products || []);
  const [isDesktop, setIsDesktop] = useState(props.layout.isAboveBreakpoint('lg'));
  const [isFilterOpen, setIsFiltersOpen] = useState(props.collectionHandle === 'customizer' ? false : isDesktop);
  const [hideProductGrid, setHideProductGrid] = useState(true);
  const [hideFilterButton, setHideFilterButton] = useState(false);

  const { openCustomizerStyleModal } = useCustomizerStyleModal();

  const panelTriggers = {
    openTriggerEl: null,
    closeTriggerEl: null,
  };

  const getCollection = async () => {
    const response = await storefront.request({
      query: queries.collectionGet,
      variables: {
        id: props.collection.id,
        first: 50,
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error('product-grid/product-grid.js: Storefront API request failed', response);
      setLoading(false);
      return [];
    }

    return edgesToNodes(response.data.collection.products, 'product');
  };

  useEffect(() => {
    if (products.length) return;
    const fetchProducts = async () => {
      const collectionProducts = await getCollection();
      setProducts(collectionProducts);
      setLoading(false);
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(props.layout.isAboveBreakpoint('lg'));
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.layout]);

  useEffect(() => {
    if (isFilterOpen && !isDesktop) {
      document.body.classList.add('scroll-lock');
    }

    return () => {
      document.body.classList.remove('scroll-lock');
    };
  }, [isFilterOpen, isDesktop]);

  useEffect(() => {
    const paginationElement = document.querySelector('.product-grid-pagination');

    // if Nosto is disabled, show the Shopify product grid and pagination
    const isNostoDisabled = () => {
      // Get the element that contains the default grid
      const productGridSection = document.querySelector('[data-section="product-grid"]');

      if (productGridSection && !productGridSection.querySelector('.nosto-serp-content')) {
        if (paginationElement) {
          paginationElement.classList.remove('hidden');

          setIsFiltersOpen(false);
          setHideFilterButton(true);
        }
        setHideProductGrid(false);
      }
    };

    const nostoLoadTimeout = setTimeout(isNostoDisabled, 3000);

    return () => {
      clearTimeout(nostoLoadTimeout);
    };
  }, []);

  const toggleFiltersPanel = () => {
    setIsFiltersOpen(!isFilterOpen);
  };

  const FiltersButton = () => (
    !hideFilterButton ? (
      <button
        className="body-16 flex w-fit items-center gap-4 rounded border border-primary p-8 text-primary"
        onClick={toggleFiltersPanel}
      >
        <FiltersIcon />
        {isFilterOpen ? window.TMW.Strings.filters.hideFilters : window.TMW.Strings.filters.filtersTitle}
      </button>
    ) : (
      <div className="flex tems-center gap-4 h-[38px] p-0"></div>
    )
  );

  const SortingButton = () => (
    <button className="body-16 flex items-center gap-4 whitespace-nowrap rounded border border-primary p-8 text-primary">
      <SortingIcon />
      <span>{window.TMW.Strings.productGrid.sortBy}</span>
    </button>
  );

  return (
    !loading &&
    (products.length > 0 ? (
      <Fragment>
        <div className={`container relative l:p-40 ${hideProductGrid ? 'hidden' : ''}`}>
          <div
            className={`sticky top-0 z-10 flex w-full items-center gap-8 border-b border-t border-grey-5 bg-white p-15 l:absolute l:right-0 l:top-40 ${isFilterOpen ? 'l:max-w-[calc(100%-375px)]' : 'l:max-w-[calc(100%-150px)]'} l:border-0 l:p-0 l:px-40`}
          >
            {!isDesktop && (
              <>
                {!hideFilterButton && (
                  <FiltersButton />
                )}
                < SortingButton />
              </>
            )}
            {props.template === 'search' && (
              <h3
                className="h3 hidden flex-grow text-primary l:block"
                dangerouslySetInnerHTML={{
                  __html: window.TMW.Strings.search.searchResultsFor.replace('{{ terms }}', props.searchTerms),
                }}
              ></h3>
            )}
            <div className="ml-auto flex items-center gap-20">
              <span className="body-12-light">
                {products.length}{' '}
                {props.template === 'search'
                  ? window.TMW.Strings.search.results
                  : window.TMW.Strings.productGrid.products}
              </span>
              {isDesktop && <SortingButton />}
            </div>
          </div>
          {isDesktop && (
            <div className={`${isFilterOpen ? 'sticky top-40' : ''}`}>
              <FiltersButton />
            </div>
          )}
          <div className="l:flex">
            <Panel
              triggers={panelTriggers}
              defaultOpen={isDesktop}
              isOpen={isFilterOpen}
              setIsOpen={setIsFiltersOpen}
              context="filters"
              showBackdrop={!isDesktop}
              additionalClasses={`w-full max-w-[335px] l:sticky l:pt-20 l:h-fit l:z-1 l:top-[80px] l:transition-none ${isFilterOpen ? 'l:min-w-[335px]' : 'l:max-w-0'}`}
              alignment="left"
            >
              <div className="relative flex h-full flex-col">
                <button
                  className="text-smokey-blue absolute right-15 top-20 l:hidden"
                  onClick={toggleFiltersPanel}
                  aria-label={window.TMW.Strings.filters.close}
                >
                  <CloseIcon />
                </button>
                <div
                  className={`flex h-auto grow flex-col overflow-y-auto pt-[55px] l:pt-0 ${isFilterOpen ? 'l:min-w-[300px]' : ''}`}
                >
                  {(isDesktop && !hideFilterButton) && <SelectedFilters />}
                  <Accordions
                    accordions={filtersArray}
                    filterAccordions={true}
                  />
                </div>
                <div className="flex flex-col items-center gap-y-12 border-t border-grey-5 px-15 py-20 l:hidden">
                  <button className="cta-black h-45 w-full justify-center rounded py-15 no-underline">
                    {window.TMW.Strings.filters.seeNumResults.replace('{{ count }}', products.length)}
                  </button>
                  <a className="body-14-light underline">{window.TMW.Strings.filters.clearAll}</a>
                </div>
              </div>
            </Panel>
            {(!isDesktop && !hideFilterButton) && <SelectedFilters />}
            <div
              className={`grid grid-cols-2 gap-x-12 gap-y-20 px-15 py-20 l:gap-20 l:pb-40 l:pr-0 ${isFilterOpen ? 'l:grid-cols-3 l:pl-40' : 'l:grid-cols-4 l:pl-0'}`}
            >
              {props.template === 'search' && (
                <h3
                  className="h4 col-span-2 text-primary l:hidden"
                  dangerouslySetInnerHTML={{
                    __html: window.TMW.Strings.search.searchResultsFor.replace('{{ terms }}', props.searchTerms),
                  }}
                ></h3>
              )}
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  openCustomizerStyleModal={openCustomizerStyleModal}
                  product={product}
                  template={props.template}
                  {...props}
                />
              ))}
            </div>
          </div>
        </div>
      </Fragment>
    ) : (
      <div className={`container p-15 l:p-40 ${hideProductGrid ? 'hidden' : ''}`}>
        {props.template === 'search' ? (
          <h3
            className="h3 text-center text-primary"
            dangerouslySetInnerHTML={{
              __html: window.TMW.Strings.search.noResultsFor.replace('{{ terms }}', props.searchTerms),
            }}
          ></h3>
        ) : (
          <h3 className="h3 text-center text-primary">{window.TMW.Strings.productGrid.noProducts}</h3>
        )}
      </div>
    ))
  );
};

// exports
export default ProductGrid;
